import { useEffect, useRef, useState } from "react";

import PrimaryButton from "../buttons/PrimaryButton";
import { useAppContext } from "../config/AppContext";
import { signInAsGuest } from "../auth/FirebaseAuth";
import ReCAPTCHA from "react-google-recaptcha";
import { useSnackbar } from "notistack";
import { Spin } from "antd";

// Checkbox ("I'm not a robot") key — the historical fallback.
const CHECKBOX_SITE_KEY = "6LfaqgErAAAAAElOWIkaMesulgq0chp-pTODwhKN";
// reCAPTCHA Enterprise (score) key — invisible, NO checkbox. When set at deploy
// the challenge runs silently on submit and the backend scores the token via
// the Enterprise assessment API (request flag enterprise:true). Until it's
// provisioned we fall back to the checkbox so nothing breaks.
const ENTERPRISE_SITE_KEY = process.env.REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY;
const RECAPTCHA_ACTION = "submit";

// Inject the Enterprise script once (idempotent).
function loadEnterpriseScript(siteKey) {
  if (typeof window === "undefined" || !siteKey) return;
  if (document.querySelector(`script[data-grecaptcha="${siteKey}"]`)) return;
  const s = document.createElement("script");
  s.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
  s.async = true;
  s.defer = true;
  s.setAttribute("data-grecaptcha", siteKey);
  document.head.appendChild(s);
}

const RecaptchaComponent = ({
  email,
  iframe,
  actionText = "Valider",
  handleSuccess,
  mobile = false,
}) => {
  const { appState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;
  const enterprise = !!ENTERPRISE_SITE_KEY;
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (enterprise) loadEnterpriseScript(ENTERPRISE_SITE_KEY);
  }, [enterprise]);

  // Run the invisible Enterprise challenge and resolve a fresh token.
  const execEnterprise = () =>
    new Promise((resolve, reject) => {
      const g = window.grecaptcha && window.grecaptcha.enterprise;
      if (!g) {
        reject(new Error("reCAPTCHA non chargé"));
        return;
      }
      g.ready(() => {
        g.execute(ENTERPRISE_SITE_KEY, { action: RECAPTCHA_ACTION })
          .then(resolve)
          .catch(reject);
      });
    });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Checkbox mode still requires the user to tick first (dev bypasses the
    // captcha via /captcha/test). Enterprise mode has nothing to tick — the
    // challenge runs silently below.
    if (!appState.dev && !enterprise && !captchaToken) {
      handleClickVariant("Merci de compléter le captcha", "error");
      return;
    }
    setLoading(true);
    try {
      // Resolve the captcha token: enterprise -> run the silent challenge;
      // checkbox -> reuse the tick token; dev -> none (test endpoint).
      let captcha = captchaToken;
      if (!appState.dev && enterprise) {
        captcha = await execEnterprise();
      }

      let res = await fetch(
        prefix + (appState.dev ? "auth/captcha/test" : "auth/captcha/check"),
        {
          method: "POST",
          body: JSON.stringify({
            token: captcha,
            master_id: iframe.master_id,
            email: email,
            enterprise: enterprise,
          }),
          headers: { "Content-Type": "application/json" },
        }
      );
      let response = await res.json();
      if (response.success) {
        let token = response.token;
        try {
          await signInAsGuest(token);
        } catch (error) {
          console.error("Error signing in as guest:", error);
          handleClickVariant("Erreur lors de la connexion", "error");
          setLoading(false);
          return;
        }
        handleSuccess();
      } else {
        handleClickVariant("Captcha invalide", "error");
      }
    } catch (error) {
      console.error("Captcha error:", error);
      handleClickVariant("Erreur captcha, réessayez", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {!enterprise && (
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={CHECKBOX_SITE_KEY}
          onChange={(token) => setCaptchaToken(token)}
        />
      )}
      <PrimaryButton
        disabled={loading}
        className={"w-fit"}
        onClick={handleSubmit}
        mobile={mobile}
      >
        {actionText}
        <Spin spinning={loading} />
      </PrimaryButton>
    </div>
  );
};
export default RecaptchaComponent;
